import { Component, OnInit, OnDestroy, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { interval, Subscription } from 'rxjs';
import { OrderService, OrderHistoryItem } from '../../services/order.service';
import { ReservationService, MyReservationItem } from '../../services/reservation.service';
import { WebSocketService } from '../../services/websocket.service';

const TERMINAL_STATUSES = ['completed', 'cancelled', 'rejected'];

@Component({
  selector: 'app-order-history',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './order-history.component.html',
  styleUrl: './order-history.component.scss'
})
export class OrderHistoryComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private orderService = inject(OrderService);
  private reservationService = inject(ReservationService);
  private wsService = inject(WebSocketService);
  private pollSub?: Subscription;
  private wsSub?: Subscription;
  private subscribedSessions = new Set<string>();

  activeTab = signal<'orders' | 'reservations'>('orders');
  orders = signal<OrderHistoryItem[]>([]);
  reservations = signal<MyReservationItem[]>([]);
  loading = signal(true);
  loadingReservations = signal(false);
  errorMsg = signal('');
  expandedOrderId = signal<string | null>(null);

  ngOnInit(): void {
    const tab = this.route.snapshot.queryParamMap.get('tab');
    if (tab === 'reservations') {
      this.activeTab.set('reservations');
    }
    this.loadOrders();
    this.loadReservations();
    this.pollSub = interval(30_000).subscribe(() => this.loadOrders(false));

    // Real-time order status updates via WebSocket
    // TPVSecure publica a orders.status.{order_id} donde order_id = tpv_order_id
    this.wsSub = this.wsService.orderStatusUpdates$.subscribe(msg => {
      const data = msg.data;
      const currentOrders = this.orders();
      const updated = currentOrders.map(order => {
        if (order.tpv_order_id === data.order_id) {
          // Auto-unsubscribe on terminal status
          if (TERMINAL_STATUSES.includes(data.status) && order.tpv_order_id) {
            this.unsubscribeOrder(order.tpv_order_id);
          }
          return {
            ...order,
            status: data.status,
            ...(data.order_ref && { order_ref: data.order_ref }),
            ...(data.estimated_minutes != null && { estimated_minutes: data.estimated_minutes }),
          };
        }
        return order;
      });
      this.orders.set(updated);
    });
  }

  ngOnDestroy(): void {
    this.pollSub?.unsubscribe();
    this.wsSub?.unsubscribe();
    // Unsubscribe all active order status sessions
    this.subscribedSessions.forEach(sid => this.wsService.unsubscribeFromOrderStatus(sid));
    this.subscribedSessions.clear();
  }

  loadOrders(showLoading = true): void {
    if (showLoading) this.loading.set(true);
    this.errorMsg.set('');

    this.orderService.getOrderHistory(50).subscribe({
      next: (res) => {
        this.loading.set(false);
        if (res.success) {
          this.orders.set(res.orders);
          this.subscribeActiveOrders(res.orders);
        } else {
          this.errorMsg.set('No se pudieron cargar los pedidos');
        }
      },
      error: () => {
        this.loading.set(false);
        this.errorMsg.set('Error de conexion');
      }
    });
  }

  /** Subscribe to WebSocket for orders that are still active (not terminal) */
  private subscribeActiveOrders(orders: OrderHistoryItem[]): void {
    for (const order of orders) {
      if (order.tpv_order_id && !TERMINAL_STATUSES.includes(order.status) && !this.subscribedSessions.has(order.tpv_order_id)) {
        this.wsService.subscribeToOrderStatus(order.tpv_order_id);
        this.subscribedSessions.add(order.tpv_order_id);
      }
    }
  }

  private unsubscribeOrder(orderId: string): void {
    if (this.subscribedSessions.has(orderId)) {
      this.wsService.unsubscribeFromOrderStatus(orderId);
      this.subscribedSessions.delete(orderId);
    }
  }

  toggleOrderDetail(orderId: string): void {
    this.expandedOrderId.set(this.expandedOrderId() === orderId ? null : orderId);
  }

  isExpanded(orderId: string): boolean {
    return this.expandedOrderId() === orderId;
  }

  isActiveOrder(status: string): boolean {
    return !TERMINAL_STATUSES.includes(status);
  }

  getStatusLabel(status: string): string {
    const labels: Record<string, string> = {
      pending: 'Pendiente',
      confirmed: 'Confirmado',
      accepted: 'Aceptado',
      in_progress: 'Preparando',
      prepared: 'Listo',
      completed: 'Completado',
      rejected: 'Rechazado',
      cancelled: 'Cancelado',
    };
    return labels[status] || status;
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'pending': return 'status-pending';
      case 'confirmed':
      case 'accepted':
      case 'in_progress': return 'status-active';
      case 'prepared':
      case 'completed': return 'status-done';
      case 'rejected':
      case 'cancelled': return 'status-cancelled';
      default: return 'status-pending';
    }
  }

  getServiceLabel(serviceType: string): string {
    const labels: Record<string, string> = {
      'dine-in': 'En local',
      'pickup': 'Para llevar',
      'delivery': 'A domicilio',
      'external': 'Externo',
    };
    return labels[serviceType] || serviceType;
  }

  getServiceIcon(serviceType: string): string {
    const icons: Record<string, string> = {
      'dine-in': '🍽️',
      'pickup': '🥡',
      'delivery': '🛵',
      'external': '📋',
    };
    return icons[serviceType] || '📋';
  }

  formatPrice(amount: string | number, currency: string): string {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(num)) return String(amount);
    return new Intl.NumberFormat('es-ES', { style: 'currency', currency }).format(num);
  }

  formatDate(iso: string): string {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMin = Math.floor(diffMs / 60_000);

    if (diffMin < 1) return 'Ahora';
    if (diffMin < 60) return `Hace ${diffMin} min`;

    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return `Hace ${diffH}h`;

    return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  }

  switchTab(tab: 'orders' | 'reservations'): void {
    this.activeTab.set(tab);
  }

  loadReservations(): void {
    this.loadingReservations.set(true);
    this.reservationService.getMyReservations(50).subscribe({
      next: (res) => {
        this.loadingReservations.set(false);
        if (res.success) {
          this.reservations.set(res.reservations);
        }
      },
      error: () => {
        this.loadingReservations.set(false);
      }
    });
  }

  getReservationStatusLabel(status: string): string {
    const labels: Record<string, string> = {
      pending: 'Pendiente',
      confirmed: 'Confirmada',
      seated: 'En mesa',
      completed: 'Completada',
      cancelled: 'Cancelada',
      no_show: 'No asistio',
    };
    return labels[status] || status;
  }

  getReservationStatusClass(status: string): string {
    switch (status) {
      case 'pending': return 'status-pending';
      case 'confirmed':
      case 'seated': return 'status-active';
      case 'completed': return 'status-done';
      case 'cancelled':
      case 'no_show': return 'status-cancelled';
      default: return 'status-pending';
    }
  }

  formatReservationDate(dateStr: string): string {
    const d = new Date(dateStr + 'T00:00:00');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffDays = Math.round((d.getTime() - today.getTime()) / 86400000);

    if (diffDays === 0) return 'Hoy';
    if (diffDays === 1) return 'Manana';
    if (diffDays === -1) return 'Ayer';

    return d.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' });
  }

  getBusinessTypeIcon(type: string): string {
    const icons: Record<string, string> = {
      restaurant: '🍽️',
      salon: '💇',
      generic: '🏢',
    };
    return icons[type] || '📅';
  }

  deleteOrder(event: Event, orderId: string): void {
    event.stopPropagation();
    if (!confirm('Eliminar este pedido del historial?')) return;
    this.orderService.deleteOrder(orderId).subscribe({
      next: (res) => {
        if (res.success) {
          this.orders.update(list => list.filter(o => o.id !== orderId));
        }
      }
    });
  }

  cancelReservation(event: Event, resId: string): void {
    event.stopPropagation();
    if (!confirm('Cancelar esta reserva?')) return;
    this.reservationService.cancelMyReservation(resId).subscribe({
      next: (res) => {
        if (res.success) {
          this.reservations.update(list =>
            list.map(r => r.id === resId ? { ...r, status: 'cancelled' } : r)
          );
        }
      }
    });
  }

  deleteReservation(event: Event, resId: string): void {
    event.stopPropagation();
    if (!confirm('Eliminar esta reserva del historial?')) return;
    this.reservationService.deleteMyReservation(resId).subscribe({
      next: (res) => {
        if (res.success) {
          this.reservations.update(list => list.filter(r => r.id !== resId));
        }
      }
    });
  }

  isTerminalReservation(status: string): boolean {
    return ['completed', 'cancelled', 'no_show'].includes(status);
  }

  goBack(): void {
    this.router.navigate(['/dashboard']);
  }
}
